import { AnalyticsStore } from 'bernie-plugin-mobx';

import { FlightsFlexibleSearchResultCellTheme, FlightsFlexibleSearchHeaderCellTheme } from '__generated__/api';
import { FlightsFlexSearchResults } from '__generated__/api/types';
import { trackAnalytics } from 'utility';
import { CheapestListingInfoStore, SearchCriteriaStore, SearchFormStore } from '../stores';

const enum TextTheme {
  POSITIVE = 'positive',
  LINK = 'link'
}

export const isFlexibleSearchCellSelected = (theme: FlightsFlexibleSearchResultCellTheme): boolean => {
  const selectedCellThemes = [
    FlightsFlexibleSearchResultCellTheme.SEARCHED_DATE,
    FlightsFlexibleSearchResultCellTheme.SEARCHED_DATE_LOWEST_PRICE,
    FlightsFlexibleSearchResultCellTheme.SEARCHED_DATE_NO_PRICE,
    FlightsFlexibleSearchResultCellTheme.SEARCHED_DATE_UNAVAILABLE_FLIGHTS
  ];

  return selectedCellThemes.indexOf(theme) > -1;
};

export const getFlexibleSearchCellTextTheme = (theme: FlightsFlexibleSearchResultCellTheme) => {
  const themeMap = {
    LOWEST_PRICE: TextTheme.POSITIVE,
    NO_PRICE: TextTheme.LINK,
    SEARCHED_DATE_LOWEST_PRICE: TextTheme.POSITIVE,
    SEARCHED_DATE_NO_PRICE: TextTheme.LINK
  };

  return themeMap[theme];
};

const enum GridResultTheme {
  DEFAULT = 'default',
  NEGATIVE = 'negative',
  POSITIVE = 'positive',
  LINK = 'link',
  SELECTED_CELL = 'selected-cell',
  SELECTED_HEADER = 'selected-header',
  UNAVAILABLE = 'unavailable'
}

export const getFlexibleSearchGridResultTheme = (
  theme: FlightsFlexibleSearchResultCellTheme | FlightsFlexibleSearchHeaderCellTheme
) => {
  const themeMap = {
    DEFAULT: GridResultTheme.DEFAULT,
    HIGHEST_PRICE: GridResultTheme.NEGATIVE,
    LOWEST_PRICE: GridResultTheme.POSITIVE,
    NO_PRICE: GridResultTheme.LINK,
    SEARCHED_DATE: GridResultTheme.SELECTED_CELL,
    SEARCHED_DATE_HEADER: GridResultTheme.SELECTED_HEADER,
    SEARCHED_DATE_HIGHEST_PRICE: GridResultTheme.SELECTED_CELL,
    SEARCHED_DATE_LOWEST_PRICE: GridResultTheme.SELECTED_CELL,
    SEARCHED_DATE_NO_PRICE: GridResultTheme.SELECTED_CELL,
    SEARCHED_DATE_UNAVAILABLE_FLIGHTS: GridResultTheme.SELECTED_CELL,
    UNAVAILABLE_FLIGHTS: GridResultTheme.UNAVAILABLE
  };

  return themeMap[theme];
};

const formatAndParsePrice = (price: string) => {
  const regexForNumbersOnly = /[^0-9]/g;
  const formattedPrice = price.replace(regexForNumbersOnly, '');

  return parseInt(formattedPrice);
};

const formatToken = (rffrToken: string) => {
  return rffrToken && rffrToken.substr(rffrToken.lastIndexOf('.') + 1);
};

const getPriceDeviationPercent = (cheapestSearchedPriceString: string, flexPriceString: string) => {
  const flexPrice = formatAndParsePrice(flexPriceString);
  const priceDifference = formatAndParsePrice(cheapestSearchedPriceString) - flexPrice;
  const priceDeviationPercent = (priceDifference / flexPrice) * 100;

  return Math.round(priceDeviationPercent).toString().concat('%');
};

const fireAnalyticsForNffOrPriceDeviation = (
  cheapestListingInfoStore: CheapestListingInfoStore,
  searchedPrice: string,
  flexPrice: string,
  analyticsStore: AnalyticsStore
) => {
  const priceNotFound = '-';
  const isFlexSearchedDateNff = flexPrice && isNaN(formatAndParsePrice(flexPrice));
  const isSearchedDateNff = searchedPrice === priceNotFound;
  const isBothSearchAndFlexDateNFF = isFlexSearchedDateNff && isSearchedDateNff;
  if ((isSearchedDateNff || isFlexSearchedDateNff) && !isBothSearchAndFlexDateNFF) {
    trackAnalytics(cheapestListingInfoStore.cheapestListingInfo.nffAnalytics, analyticsStore);
  }
  if (!isFlexSearchedDateNff && !isSearchedDateNff) {
    const { deviationAnalytics } = cheapestListingInfoStore.cheapestListingInfo;
    const rfrrToken = deviationAnalytics && formatToken(deviationAnalytics.referrerId);
    const priceDeviationPercentage = getPriceDeviationPercent(searchedPrice, flexPrice);
    trackAnalytics(deviationAnalytics, analyticsStore, rfrrToken, priceDeviationPercentage);
  }
};

export const getValueText = (
  cheapestListingInfoStore: CheapestListingInfoStore,
  flexPrice: string,
  theme: FlightsFlexibleSearchResultCellTheme,
  analyticsStore?: AnalyticsStore
) => {
  const cellSelected = isFlexibleSearchCellSelected(theme);
  const searchedPrice = cheapestListingInfoStore?.cheapestListingInfo?.price;

  if (searchedPrice && cellSelected && searchedPrice !== flexPrice) {
    if (analyticsStore) {
      fireAnalyticsForNffOrPriceDeviation(cheapestListingInfoStore, searchedPrice, flexPrice, analyticsStore);
    }

    return searchedPrice;
  }

  return flexPrice;
};

export function prepareStoresForNewSearch(
  searchCriteriaStore: SearchCriteriaStore,
  searchFormStore: SearchFormStore,
  nextSearchCriteria: FlightsFlexSearchResults.NextSearchCriteria
) {
  searchCriteriaStore.syncWithNextSearchCriteria(nextSearchCriteria);
  searchCriteriaStore.clearSortFilterSearchCriteria();
  searchCriteriaStore.clearJourneysContinuationId();
  searchCriteriaStore.clearPagination();
  searchFormStore.syncJourneysWithSearchCriteriaStore(searchCriteriaStore);
}
